import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import {
  componentDestroyed,
  GameStatuses,
  GameTimelineStages,
  generateArray, handleError,
  LeagueCourt,
  Playoff,
  Tournament,
  TournamentEventTypes,
  TournamentGroup,
  TournamentRound,
  TournamentRoundTeam,
  TournamentRoundTypes,
  TournamentStatuses,
  TournamentTypes,
  UntilDestroy
} from '@mtgame/core';
import { TournamentRootService } from '@core/services/tournament-root.service';
import { map, take, takeUntil } from 'rxjs/operators';
import { GamesHeadlineService } from '../../../shared/games-headline/services/games-headline.service';
import { SeoService } from '@core/services/seo.service';
import { LayoutService } from '../../layout/layout.service';
import { isPlatformBrowser } from '@angular/common';
import { getTourTitle } from '@utils/hardcode';
import { combineLatest } from 'rxjs';
import { LoaderService, ToastService } from '@mtgame/ui';
import { ActivatedRoute, Router } from '@angular/router';
import { DIVISION_QUERY_PARAM, ROUND_QUERY_PARAM } from '@config/routing-map';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-home',
  templateUrl: './tournament-home.component.html',
  styleUrls: ['./tournament-home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentHomeComponent implements OnInit, OnDestroy {
  tournament: Tournament;
  rounds: TournamentRound[];
  selectedRound: TournamentRound;
  gamesStages: GameTimelineStages;
  groups: TournamentGroup[];
  divisionGroups: {[key: number]: TournamentGroup[]};
  playoffs: Playoff[];
  divisionPlayoffs: {[key: number]: Playoff[]};
  tours: number[];
  eventTypes = TournamentEventTypes;
  tournamentStatuses = TournamentStatuses;
  courts: LeagueCourt[] = [];
  isBrowser = isPlatformBrowser(this.platformId);
  tournamentTypes = TournamentTypes;
  gameStatuses = GameStatuses;
  roundTypes = TournamentRoundTypes;
  divisionId: number;
  isChess: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private tournamentRootService: TournamentRootService,
    private gamesHeadlineService: GamesHeadlineService,
    private seoService: SeoService,
    private layoutService: LayoutService,
    private loaderService: LoaderService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.tournamentRootService.tournament$
      .pipe(take(1))
      .subscribe(tournament => {
        if (tournament.cover.path) {
          this.layoutService.setCover(tournament.cover.path);
        }
      });
    this.tournamentRootService.tournament$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(tournament => {
        this.tournament = tournament;
        this.seoService.setDefaultData({
          title: tournament.name,
          description: tournament.description,
          image: tournament.logo.path
        });
        this.cdr.markForCheck();
      });
    combineLatest([
      this.tournamentRootService.rounds$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentRootService.selectedRound$.pipe(takeUntil(componentDestroyed(this))),
      this.route.queryParams.pipe(map(params => +params[DIVISION_QUERY_PARAM])),
    ]).subscribe(([rounds, selectedRound, divisionId]) => {
      this.rounds = rounds.filter(item => !divisionId || item.divisions.find(division => division.id === divisionId));
      this.selectedRound = selectedRound;
      this.cdr.markForCheck();
    });
    this.tournamentRootService.gamesStages$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(gamesStages => {
        this.gamesStages = gamesStages;
        this.tours = generateArray(gamesStages.currentTour);
        this.cdr.markForCheck();
      });
    this.tournamentRootService.courts$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(courts => {
        this.courts = courts;
        this.cdr.markForCheck();
      });
    this.tournamentRootService.groups$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(groups => {
        this.groups = groups;
        this.divisionGroups = groups.reduce((acc, item) => ({
          ...acc,
          [item.divisionId]: [
            ...(acc[item.divisionId] || []),
            item
          ]
        }), []);
        this.cdr.markForCheck();
      });
    this.tournamentRootService.playoffs$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(playoffs => {
        this.playoffs = playoffs;
        this.divisionPlayoffs = playoffs.reduce((acc, item) => ({
          ...acc,
          [item.divisionId]: [
            ...(acc[item.divisionId] || []),
            item
          ]
        }), []);
        this.cdr.markForCheck();
      });
    this.route.queryParams.subscribe(query => {
      this.divisionId = +query[DIVISION_QUERY_PARAM];
      this.cdr.markForCheck();
      window.scrollTo(0, 0);
    });
    if (this.isBrowser) {
      this.tournamentRootService.getCourts();
    }
  }

  ngOnDestroy(): void {
    this.layoutService.setCover('');
    this.tournamentRootService.dispose();
  }

  getTourTitle(tour: number): string {
    return getTourTitle(tour, this.tournament.sport);
  }

  async setRound(round: TournamentRound): Promise<void> {
    this.loaderService.show();
    try {
      await this.tournamentRootService.setRound(round);
    } catch (e) {
      handleError(e);
      this.toastService.danger('Ошибка загрузки');
    } finally {
      this.loaderService.hide();
    }
  }

  getRoundQueryParams(roundId: number) {
    return {[ROUND_QUERY_PARAM]: roundId};
  }

  getDivisionQueryParams(divisionId: number) {
    return {[DIVISION_QUERY_PARAM]: divisionId};
  }

  chess(value: boolean): void {
    this.isChess = value;
  }
}
